/**
 * Which bars the chart shows: a range `[start, end)` over the loaded bars, in bar units.
 * Zoom and pan are arithmetic on it, kept here so they can be tested without a pointer.
 *
 * The range is fractional and only rounded where bars are picked to draw, by `visibleRange`.
 * A trackpad sends many small wheel events; rounded one at a time, each would come back
 * unchanged and a slow gesture would do nothing at all. Kept fractional, they add up.
 */
export interface ChartWindow {
  readonly start: number;
  readonly end: number;
}

/** Zoomed in no further than this many bars: past it a candle is wider than it is informative. */
export const MIN_VISIBLE_BARS = 10;

export function fullWindow(total: number): ChartWindow {
  return { start: 0, end: total };
}

/** The whole bars a window covers: the indices to slice, `[start, end)`. */
export function visibleRange(window: ChartWindow, total: number): ChartWindow {
  const start = clamp(Math.round(window.start), 0, total);
  const end = clamp(start + Math.round(window.end - window.start), start, total);
  return { start, end };
}

export function isFullWindow(window: ChartWindow, total: number): boolean {
  const range = visibleRange(window, total);
  return range.start === 0 && range.end === total;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/**
 * Scales the window by `factor` — above 1 zooms out, below 1 zooms in — keeping the bar under
 * the pointer where it is. `anchor` is the pointer's position across the plot, 0 at the left
 * edge and 1 at the right, so zooming at the right edge keeps the latest bars in view.
 *
 * Returns the very same window when it cannot move — fully out, or fully in — which is how a
 * caller tells whether this event was the chart's to take.
 */
export function zoomWindow(window: ChartWindow, total: number, anchor: number, factor: number): ChartWindow {
  const size = window.end - window.start;
  const minimum = Math.min(MIN_VISIBLE_BARS, total);
  const nextSize = clamp(size * factor, minimum, total);
  if (nextSize === size) {
    return window;
  }
  const at = clamp(anchor, 0, 1);
  const pivot = window.start + at * size;
  const start = clamp(pivot - at * nextSize, 0, total - nextSize);
  return { start, end: start + nextSize };
}

/** Shifts the window by bars, positive toward later dates, stopping at either end. The same window when it cannot move. */
export function panWindow(window: ChartWindow, total: number, bars: number): ChartWindow {
  const size = window.end - window.start;
  const start = clamp(window.start + bars, 0, total - size);
  return start === window.start ? window : { start, end: start + size };
}
